import React, { FC } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ConfirmPopUp, Checkbox } from "application/components";
import { useAppSelector } from "application/store";
import { formatTotal } from "application/helpers/formatTotal";
import { Divider } from "application/components";

import { confirmCommitmentSchema } from "features/invest/helpers/validation";

import ReferralBonus from "../ReferralBonus";

import { ConfirmModalProps, FormDataDto } from "./models";
import {
  ModalStyled,
  AmountStyled,
  CommitmentFlow,
  OvercommitmentFlow,
  Container,
  RowStyled,
  BottomSection,
} from "./styles";

const supportLink = process.env.REACT_APP_COMMITMENT_TERMS_SUPPORT_URL || "";

const ConfirmModal: FC<ConfirmModalProps> = ({
  isLoading,
  opened,
  onCancel,
  onSubmit,
}) => {
  const { deal, commitment } = useAppSelector((state) => state.deal);

  const currency = deal?.investmentCurrency;
  const subTitle = `To the "${deal?.title}" startup.`;
  const amount = formatTotal(commitment?.amount, currency);
  const commitmentAmount = formatTotal(commitment?.commitment, currency);
  const overcommitmentAmount = formatTotal(
    commitment?.overcommitment,
    currency
  );
  const showReferralBonus = commitment.bonusBalance > 0;

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<FormDataDto>({
    defaultValues: {
      isConditionsAccepted: false,
      isInvestmentConfirmed: false,
    },
    resolver: yupResolver(confirmCommitmentSchema),
  });

  const hasErrors = !!Object.keys(errors).length;

  const handleAcceptConditions = (value: boolean) => {
    setValue("isConditionsAccepted", value, {
      shouldValidate: !!errors.isConditionsAccepted,
    });
  };

  const handleConfirmInvestment = (value: boolean) => {
    setValue("isInvestmentConfirmed", value, {
      shouldValidate: !!errors.isInvestmentConfirmed,
    });
  };

  return (
    <ConfirmPopUp
      isLoading={isLoading}
      opened={opened}
      title="Finalize your commitment"
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      disabled={hasErrors}
    >
      <ModalStyled>
        {commitment?.overcommitment ? (
          <OvercommitmentFlow>
            <p>{subTitle}</p>
            <Container>
              <RowStyled className="bold">
                <h2>Confirmed commitment:</h2>
                <div>
                  <h3>{commitmentAmount}</h3>
                </div>
              </RowStyled>
              <RowStyled className="bold">
                <h2>Commitment to be confirmed by ICLUB:</h2>
                <div>
                  <h3>{overcommitmentAmount}</h3>
                </div>
              </RowStyled>
              <Divider />
              <RowStyled className="bold">
                <h2>Total:</h2>
                <div>
                  <AmountStyled>{amount}</AmountStyled>
                </div>
              </RowStyled>
              {showReferralBonus && <ReferralBonus />}
            </Container>
          </OvercommitmentFlow>
        ) : (
          <CommitmentFlow>
            <p>{subTitle}</p>
            <AmountStyled>{amount}</AmountStyled>
            {showReferralBonus && (
              <Container>
                <ReferralBonus />
              </Container>
            )}
          </CommitmentFlow>
        )}
        <BottomSection>
          <Checkbox
            id="isConditionsAccepted"
            label={
              <p>
                I understand that my commitment is{" "}
                <a href={supportLink} target="_blank">
                  non-cancelable
                </a>{" "}
                and must be paid.{" "}
              </p>
            }
            value={watch("isConditionsAccepted")}
            onChange={(e) => handleAcceptConditions(e.target.checked)}
            hasError={!!errors.isConditionsAccepted}
          />
          <Checkbox
            id="isInvestmentConfirmed"
            label="I confirm that my investment funds are not in a Ukrainian bank and that I have an EU bank account or a neobank account (Wise, Revolut, Zen, etc.)."
            value={watch("isInvestmentConfirmed")}
            onChange={(e) => handleConfirmInvestment(e.target.checked)}
            hasError={!!errors.isInvestmentConfirmed}
          />
        </BottomSection>
      </ModalStyled>
    </ConfirmPopUp>
  );
};

export default ConfirmModal;
